import { RESOLUTION_LINES, RESOLUTION_PROFILES, type ResolutionProfile } from "../config.ts";

export interface HudStats {
  fps: number;
  rttMs: number | null;
  renderWidth: number;
  renderHeight: number;
  players: number;
  inFlight: number;
  /** Meshes drawn this frame: roughly the draw calls, the main cost on phones. */
  meshes: number;
}

/** Debug line, quick toggles and the connection status overlay. */
export class Hud {
  onResolutionChange: (profile: ResolutionProfile) => void = () => {};
  onRetry: () => void = () => {};

  private readonly debugLine = element("debug-line");
  private readonly resButton = element<HTMLButtonElement>("res-button");
  private readonly fullscreenButton = element<HTMLButtonElement>("fullscreen-button");
  private readonly status = element("status");
  private readonly statusText = element("status-text");
  private readonly retry = element<HTMLButtonElement>("status-retry");
  private lastText = "";
  private resolution: ResolutionProfile;

  constructor(resolution: ResolutionProfile) {
    this.resolution = resolution;
    this.renderResolution();
    this.resButton.addEventListener("click", () => {
      const next = RESOLUTION_PROFILES[(RESOLUTION_PROFILES.indexOf(this.resolution) + 1) % RESOLUTION_PROFILES.length]!;
      this.resolution = next;
      this.renderResolution();
      this.onResolutionChange(next);
    });

    if (document.fullscreenEnabled) {
      this.fullscreenButton.hidden = false;
      this.fullscreenButton.addEventListener("click", () => void toggleFullscreen());
    }
    this.retry.addEventListener("click", () => this.onRetry());
  }

  update(stats: HudStats): void {
    const rtt = stats.rttMs === null ? "–" : `${Math.round(stats.rttMs)}`;
    const text =
      `${Math.round(stats.fps)} fps · rtt ${rtt} ms · in volo ${stats.inFlight}\n` +
      `${stats.renderWidth}×${stats.renderHeight} px · mesh ${stats.meshes} · giocatori ${stats.players}`;
    if (text !== this.lastText) {
      this.debugLine.textContent = text;
      this.lastText = text;
    }
  }

  showStatus(message: string, canRetry = false): void {
    this.statusText.textContent = message;
    this.retry.hidden = !canRetry;
    this.status.hidden = false;
  }

  hideStatus(): void {
    this.status.hidden = true;
  }

  private renderResolution(): void {
    const lines = RESOLUTION_LINES[this.resolution];
    this.resButton.textContent = lines === null ? "HD" : `${lines}p`;
  }
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    await document.documentElement.requestFullscreen({ navigationUI: "hide" });
    // Not part of every DOM typing, and unsupported on iOS: best effort only.
    const orientation = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    await orientation.lock?.("landscape").catch(() => {});
  } catch {
    /* the browser refused; nothing else to do */
  }
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el as T;
}
